// customerdashboard.css

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import "./CustomerDashboard.css";

const CustomerDashboard = () => {
  const [salesData, setSalesData] = useState(null);
  const navigate = useNavigate();
  const { isLoggedIn, mobile } = useAuth();

  // Function to calculate the yearly incentive
  const calculateYearlyIncentive = (yearlyTarget) => {
    return Math.round(yearlyTarget * 100000 * 0.03); // 3% of the yearly target
  };

  // Function to calculate the monthly incentive
  const calculateMonthlyIncentive = (monthlyTarget) => {
    return Math.round(monthlyTarget * 100000 * 0.02); // 2% of the monthly target
  };

  // Function to format the last_updated date
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  useEffect(() => {
    if (isLoggedIn && mobile) {
      axios
        .get(`${process.env.REACT_APP_API_URL}/api/salesdata/${mobile}`, {
          withCredentials: true,
        })
        .then((response) => {
          if (response.data.length > 0) {
            const firstEntry = response.data[0];
            // Assuming we want to capitalize the first letter of each property
            let capitalizedFirstEntry = {};
            for (let key in firstEntry) {
              capitalizedFirstEntry[key] =
                typeof firstEntry[key] === "string"
                  ? firstEntry[key]
                      .toString()
                      .toLowerCase()
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ")
                  : firstEntry[key]; // Handle non-string properties
            }
            setSalesData(capitalizedFirstEntry);
          }
        })
        .catch((error) => {
          console.error("Error fetching data", error);
        });
    }
  }, [isLoggedIn, mobile]);

  // Function to calculate the percentage of the target achieved
  const calculatePercentage = (current, target) => {
    return Math.min(100, (current / target) * 100).toFixed(2);
  };

  return (
    <div className="dashboard">
      <div className="header-section">
        <h1>{salesData ? salesData.Party_Name : "Customer Dashboard"}</h1>
        <div className="header-buttons">
          <button className="invoice-button" onClick={() => navigate("/")}>
            Place Order
          </button>
          <button
            className="invoice-button"
            onClick={() => navigate("/complaint-form")}
          >
            Submit Complaint
          </button>
          <button
            className="invoice-button"
            onClick={() => navigate("/invoices")}
          >
            Download Previous Bills
          </button>
        </div>
      </div>
      <div className="detail-blocks-container">
        {salesData ? (
          <div className="customer-details-container">
            <div className="detail-block">
              <p>
                <strong>Employee:</strong> {salesData.Employee}
              </p>
              <p>
                <strong>City:</strong> {salesData.City}
              </p>
              <p>
                <strong>State:</strong> {salesData.State}
              </p>
            </div>
            <div className="detail-block">
              <h2>
                Yearly Target & Sales<div>(2024-2025)</div>
              </h2>
              <div className="progress-bar-container">
                <CircularProgressbar
                  value={calculatePercentage(
                    salesData.Total_Syn_Sales_Yearly,
                    salesData.Yearly_Target * 100000
                  )}
                  text={`${calculatePercentage(
                    salesData.Total_Syn_Sales_Yearly,
                    salesData.Yearly_Target * 100000
                  )}%`}
                  styles={buildStyles({
                    pathColor: `rgba(62, 152, 199, ${
                      calculatePercentage(
                        salesData.Total_Syn_Sales_Yearly,
                        salesData.Yearly_Target * 100000
                      ) / 100
                    })`,
                    textColor: "#f88",
                    trailColor: "#d6d6d6",
                    backgroundColor: "#3e98c7",
                  })}
                />
              </div>
              <p>
                <strong>Yearly Target (Without GST) :</strong> ₹
                {Math.round(salesData.Yearly_Target * 100000)}/-
              </p>
              <p>
                <strong>Yearly Incentive:</strong> ₹
                {calculateYearlyIncentive(salesData.Yearly_Target)}/-
              </p>
              <p>
                <strong>Current Year Sales:</strong> ₹
                {Math.round(salesData.Total_Syn_Sales_Yearly)}/-
              </p>
              <p>
                <strong>Last Updated:</strong>{" "}
                {formatDate(salesData.last_updated)}
              </p>
            </div>
            <div className="detail-block">
              <h2>
                Monthly Target & Sales
                <div>({process.env.REACT_APP_CURRENT_MONTH} 2024)</div>
              </h2>
              <div className="progress-bar-container">
                <CircularProgressbar
                  value={calculatePercentage(
                    salesData.Total_Syn_Sales_Monthly,
                    salesData.Monthly_Target * 100000
                  )}
                  text={`${calculatePercentage(
                    salesData.Total_Syn_Sales_Monthly,
                    salesData.Monthly_Target * 100000
                  )}%`}
                  styles={buildStyles({
                    pathColor: `rgba(255, 68, 0, ${
                      calculatePercentage(
                        salesData.Total_Syn_Sales_Monthly,
                        salesData.Monthly_Target * 100000
                      ) / 100
                    })`,
                    textColor: "#f88",
                    trailColor: "#d6d6d6",
                    backgroundColor: "#3e98c7",
                  })}
                />
              </div>
              <p>
                <strong>Monthly Target (Without GST) :</strong> ₹
                {Math.round(salesData.Monthly_Target * 100000)}/-
              </p>
              <p>
                <strong>Monthly Incentive:</strong> ₹
                {calculateMonthlyIncentive(salesData.Monthly_Target)}/-
              </p>
              <p>
                <strong>Current Month Sales:</strong> ₹
                {Math.round(salesData.Total_Syn_Sales_Monthly)}/-
              </p>
              {salesData.Total_Syn_Sales_Monthly >=
                salesData.Monthly_Target * 100000 * 0.99 && (
                <p>
                  <strong>Current Month Incentive:</strong> ₹
                  {Math.round(salesData.Total_Syn_Sales_Monthly * 0.02)}
                  /-
                </p>
              )}
              <p className="highlighted-row">
                <strong>Freight Free Target (Without GST):</strong> ₹
                {Math.round(salesData.Monthly_Target * 1.2 * 100000)}
                /-
              </p>
              <p>
                <strong>Last Updated:</strong>{" "}
                {formatDate(salesData.last_updated)}
              </p>
            </div>
          </div>
        ) : (
          <p>No sales data available.</p>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
